import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../utils/apiConfig';

const PurgeSettingsTab = ({ token }) => {
  const [policies, setPolicies] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [storageStats, setStorageStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [purging, setPurging] = useState(false);
  const [message, setMessage] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });

  const fetchStorageStats = async () => {
    try {
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/api/admin/purge/storage`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const result = await response.json();
      if (result.success) {
        setStorageStats(result.data);
      }
    } catch (err) {
      console.error('Erreur chargement storage stats:', err);
    }
  };

  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        const response = await fetch(
          `${API_ENDPOINTS.BASE_URL}/api/admin/purge/policies`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const result = await response.json();
        if (result.success) {
          setPolicies(result.data.policies);
          setLastRun(result.data.lastRun);
        }
      } catch (err) {
        console.error('Erreur chargement policies:', err);
        setMessage({ type: 'error', text: 'Erreur lors du chargement' });
      } finally {
        setLoading(false);
      }
    };
    fetchPolicies();
    fetchStorageStats();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/purge/policies`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ policies })
      });
      const result = await response.json();
      if (result.success) {
        setMessage({ type: 'success', text: 'Sauvegarde reussie' });
        setPolicies(result.data);
      } else {
        setMessage({ type: 'error', text: result.error || 'Erreur' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur de sauvegarde' });
    } finally {
      setSaving(false);
    }
  };

  const handleManualPurge = () => {
    setConfirmModal({
      show: true,
      title: 'Exécuter la purge',
      message: 'Exécuter la purge maintenant ?\n\nSuppression définitive des données anciennes.',
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, show: false });
        setPurging(true);
        setMessage(null);
        try {
          const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/purge/run`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const result = await response.json();
          if (result.success) {
            const total = Object.values(result.data).reduce((a, b) => a + b, 0);
            setMessage({ type: 'success', text: `${total} elements supprimes` });
            const refreshResponse = await fetch(
              `${API_ENDPOINTS.BASE_URL}/api/admin/purge/policies`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const refreshResult = await refreshResponse.json();
            if (refreshResult.success) setLastRun(refreshResult.data.lastRun);
            fetchStorageStats();
          } else {
            setMessage({ type: 'error', text: result.error || 'Erreur' });
          }
        } catch (err) {
          setMessage({ type: 'error', text: 'Erreur de purge' });
        } finally {
          setPurging(false);
        }
      }
    });
  };

  const categoryLabels = {
    analytics: { label: 'Analytics', icon: '📊' },
    presence: { label: 'Presence', icon: '👁️' },
    perf: { label: 'Performance', icon: '⚡' },
    audit: { label: 'Audit', icon: '📝' },
    error_logs: { label: 'Erreurs', icon: '🐛' }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Jamais';
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Chargement...</div>;
  if (!policies) return <div style={{ background: '#fff3cd', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>Migration Phase 5 requise</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Message */}
      {message && (
        <div style={{
          padding: '8px 16px',
          borderRadius: '6px',
          background: message.type === 'success' ? '#e8f5e9' : '#ffebee',
          color: message.type === 'success' ? '#2e7d32' : '#c62828',
          fontSize: '13px'
        }}>
          {message.text}
        </div>
      )}

      {/* Stats + Last Run en ligne */}
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        {/* Stockage */}
        {storageStats && (
          <div style={{
            flex: 2,
            minWidth: '300px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '16px',
            borderRadius: '12px',
            color: 'white'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center', marginBottom: '12px' }}>
              <div>
                <div style={{ fontSize: '22px', fontWeight: '700' }}>{storageStats.database?.size || '-'}</div>
                <div style={{ fontSize: '11px', opacity: 0.8 }}>Base totale</div>
              </div>
              {storageStats.tables?.map(table => (
                <div key={table.name}>
                  <div style={{ fontSize: '22px', fontWeight: '700' }}>{table.rowCount.toLocaleString('fr-FR')}</div>
                  <div style={{ fontSize: '11px', opacity: 0.8 }}>
                    {table.name === 'user_events' ? 'Events' : 'Errors'} ({table.size})
                  </div>
                </div>
              ))}
            </div>
            {storageStats.categories?.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center' }}>
                {storageStats.categories.map(cat => (
                  <span key={cat.name} style={{
                    background: 'rgba(255,255,255,0.2)',
                    padding: '3px 10px',
                    borderRadius: '12px',
                    fontSize: '11px'
                  }}>
                    {categoryLabels[cat.name]?.icon} {cat.count.toLocaleString('fr-FR')}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Derniere purge */}
        <div style={{
          flex: 1,
          minWidth: '200px',
          background: '#e3f2fd',
          padding: '16px',
          borderRadius: '12px'
        }}>
          <div style={{ fontSize: '12px', color: '#1565c0', marginBottom: '4px' }}>Derniere purge</div>
          <div style={{ fontWeight: '600', color: '#0d47a1', fontSize: '14px' }}>{formatDate(lastRun?.timestamp)}</div>
          {lastRun?.duration_ms && (
            <div style={{ fontSize: '11px', color: '#1976d2', marginTop: '4px' }}>
              Duree: {lastRun.duration_ms}ms
            </div>
          )}
          {lastRun?.deleted_counts && Object.values(lastRun.deleted_counts).some(c => c > 0) && (
            <div style={{ fontSize: '11px', color: '#1565c0', marginTop: '6px' }}>
              {Object.entries(lastRun.deleted_counts)
                .filter(([_, count]) => count > 0)
                .map(([cat, count]) => `${categoryLabels[cat]?.icon || cat}: ${count}`)
                .join(' · ')}
            </div>
          )}
        </div>
      </div>

      {/* Politiques en grille */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '12px'
      }}>
        {Object.entries(policies).map(([category, config]) => (
          <div
            key={category}
            style={{
              background: 'white',
              borderRadius: '10px',
              padding: '12px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              opacity: config.enabled ? 1 : 0.6
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontWeight: '600', fontSize: '13px' }}>
                {categoryLabels[category]?.icon} {categoryLabels[category]?.label || category}
              </span>
              <label style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={config.enabled}
                  onChange={(e) => setPolicies(p => ({
                    ...p,
                    [category]: { ...config, enabled: e.target.checked }
                  }))}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
              </label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="number"
                min="1"
                max="3650"
                value={config.retention_days}
                onChange={(e) => setPolicies(p => ({
                  ...p,
                  [category]: { ...config, retention_days: parseInt(e.target.value) || 1 }
                }))}
                style={{
                  width: '60px',
                  padding: '6px 8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  textAlign: 'center',
                  fontSize: '13px'
                }}
              />
              <span style={{ color: '#666', fontSize: '12px' }}>jours</span>
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '10px 20px',
            background: saving ? '#ccc' : '#4caf50',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            fontWeight: '500'
          }}
        >
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </button>
        <button
          onClick={handleManualPurge}
          disabled={purging}
          style={{
            padding: '10px 20px',
            background: purging ? '#ccc' : '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: purging ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            fontWeight: '500'
          }}
        >
          {purging ? 'Purge...' : 'Purger maintenant'}
        </button>
        <span style={{ fontSize: '11px', color: '#999', marginLeft: 'auto' }}>
          Purge auto: toutes les 24h
        </span>
      </div>

      {/* Modal de confirmation */}
      {confirmModal.show && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 2000, padding: '20px'
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', padding: '24px',
            maxWidth: '400px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600' }}>{confirmModal.title}</h3>
            <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#666', whiteSpace: 'pre-wrap' }}>{confirmModal.message}</p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmModal({ ...confirmModal, show: false })}
                style={{ padding: '10px 20px', background: '#f5f5f5', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                Annuler
              </button>
              <button onClick={confirmModal.onConfirm}
                style={{ padding: '10px 20px', background: '#f44336', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>
                Exécuter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurgeSettingsTab;
